const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function cronToHuman(expression) {
  if (!expression) return "—";

  const stripped = expression.replace(/^CRON_TZ=\S+\s+/, "");
  const parts = stripped.split(/\s+/);
  if (parts.length < 5) return expression;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  const parsedHour = parseInt(hour, 10);
  const parsedMinute = parseInt(minute, 10);
  if (isNaN(parsedHour) || isNaN(parsedMinute)) return expression;
  const time = `${String(parsedHour).padStart(2, "0")}:${String(parsedMinute).padStart(2, "0")}`;

  if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    return `Daily at ${time} UTC`;
  }

  if (dayOfMonth === "*" && month === "*" && dayOfWeek !== "*") {
    if (/^\d+$/.test(dayOfWeek)) {
      const dayName = DAYS[parseInt(dayOfWeek, 10)] || dayOfWeek;
      return `Weekly on ${dayName} at ${time} UTC`;
    }
    const dayNames = dayOfWeek.split(",").map((d) => {
      const trimmed = d.trim();
      if (/^\d+$/.test(trimmed)) return DAYS[parseInt(trimmed, 10)] || trimmed;
      if (/^\d+-\d+$/.test(trimmed)) {
        const [start, end] = trimmed.split("-").map(Number);
        return `${DAYS[start] || start}–${DAYS[end] || end}`;
      }
      return trimmed;
    });
    return `Weekly on ${dayNames.join(", ")} at ${time} UTC`;
  }

  if (dayOfMonth !== "*" && month === "*" && dayOfWeek === "*") {
    const suffix =
      dayOfMonth === "1" || dayOfMonth === "21" || dayOfMonth === "31"
        ? "st"
        : dayOfMonth === "2" || dayOfMonth === "22"
        ? "nd"
        : dayOfMonth === "3" || dayOfMonth === "23"
        ? "rd"
        : "th";
    return `Monthly on the ${dayOfMonth}${suffix} at ${time} UTC`;
  }

  return expression;
}

export function formatDuration(ms) {
  if (!ms && ms !== 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

export function formatRelativeTime(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) return "—";
  const now = new Date();
  const diffMs = now - date;
  const absDiff = Math.abs(diffMs);
  const isFuture = diffMs < 0;

  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(absDiff / 3600000);
  const days = Math.floor(absDiff / 86400000);

  let label;
  if (minutes < 1) label = "just now";
  else if (minutes < 60) label = `${minutes}m`;
  else if (hours < 24) label = `${hours}h`;
  else label = `${days}d`;

  if (label === "just now") return label;
  return isFuture ? `In ${label}` : `${label} ago`;
}

// Maps a pipeline job's schedule row to the badge the pipeline view renders.
// "stopped" is its own state — a run that was cancelled, or a job a dead
// process left on "running" that an operator cleared — and must not read as
// Idle, which looks like an untouched job that ignores clicks.
export function resolveJobStatus(job) {
  if (job.last_run_status === "running") return { badge: "running", label: "Running" };
  if (job.current_status === "paused") return { badge: "stopped", label: "Paused" };
  if (job.last_run_status === "failed") return { badge: "failed", label: "Failed" };
  if (job.last_run_status === "success") return { badge: "success", label: "Succeeded" };
  if (job.last_run_status === "stopped") return { badge: "stopped", label: "Stopped" };
  return { badge: "pending", label: "Idle" };
}

// Summarizes a stage's jobs into the badge on the stage row. Same doctrine
// as resolveJobStatus: "stopped" is its own state, never Idle.
export function stageSummary(jobs) {
  const total = jobs?.length || 0;
  if (total === 0) return { jobsLabel: "No jobs", lastRun: "—", badge: "pending", label: "Idle" };

  const running = jobs.filter((j) => j.last_run_status === "running").length;
  const paused = jobs.filter((j) => j.current_status === "paused").length;
  const failed = jobs.filter((j) => j.last_run_status === "failed").length;
  const succeeded = jobs.filter((j) => j.last_run_status === "success").length;
  const stopped = jobs.filter((j) => j.last_run_status === "stopped").length;

  const lastRun = jobs.map((j) => j.last_run_timestamp).filter(Boolean).sort().pop();

  let badge, label;
  if (running > 0) { badge = "running"; label = `${running} running`; }
  else if (paused === total) { badge = "stopped"; label = "Paused"; }
  else if (failed > 0) { badge = "failed"; label = `${failed} failed`; }
  else if (succeeded === total && succeeded > 0) { badge = "success"; label = "Succeeded"; }
  else if (succeeded > 0) { badge = "success"; label = `${succeeded}/${total} succeeded`; }
  else if (stopped > 0) { badge = "stopped"; label = "Stopped"; }
  else { badge = "pending"; label = "Idle"; }

  return {
    jobsLabel: `${total} ${total === 1 ? "job" : "jobs"}`,
    lastRun: lastRun ? formatRelativeTime(lastRun) : "—",
    badge,
    label,
  };
}
